import { useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import { AppModal } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";

const MESSAGE = "This feature is on its way. We'll have it ready for you shortly.";

/**
 * The platform's tiny "coming soon" popup: an icon, "Coming soon" and one
 * generic line (never feature-specific), with a single button to close it.
 * Use it on any button for something that isn't built yet.
 */
export function ComingSoonModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <AppModal open={open} onOpenChange={(next) => { if (!next) onClose(); }} width={320} centered hideCloseButton>
      <div className="flex flex-col items-center px-2 pb-1 pt-2 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E4002B]/10">
          <Icon icon="lucide:sparkles" className="h-6 w-6 text-[#E4002B]" />
        </span>
        <p className="!mb-0 !mt-4 text-base font-bold text-[#0A2947]">Coming soon</p>
        <p className="!mb-0 !mt-1.5 text-sm leading-6 text-[#6B7280]">{MESSAGE}</p>
        <Button
          onClick={onClose}
          className="!mt-5 !h-10 !w-full !rounded-lg !border-transparent !bg-[#E4002B]/50 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
        >
          Got it
        </Button>
      </div>
    </AppModal>
  );
}

/**
 * One-line use from any button:
 *   const comingSoon = useComingSoon();
 *   <Button onClick={comingSoon.show}>…</Button>
 *   {comingSoon.modal}
 */
export function useComingSoon() {
  const [open, setOpen] = useState(false);
  const show = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  return { show, modal: <ComingSoonModal open={open} onClose={close} /> };
}
